import React, { useState } from 'react';
import { getDatabase, ref as dbRef, push, set } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { useFont } from '../../Design/FontContext';
import './CustomFontUploader.css';

/**
 * Custom Font Uploader Component
 * Allows users to upload their own fonts for use in their spaces
 */
const CustomFontUploader = ({ onFontUploaded }) => {
    const [fontFiles, setFontFiles] = useState([]); // Multiple files for different weights
    const [fontName, setFontName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'multi'

    const auth = getAuth();
    const db = getDatabase();
    const storage = getStorage();
    const fontContext = useFont();

    // Supported font formats
    const supportedFormats = ['.ttf', '.otf', '.woff', '.woff2'];
    
    // Font weight definitions
    const fontWeights = {
        'thin': { value: 100, label: 'Thin (100)' },
        'light': { value: 300, label: 'Light (300)' },
        'regular': { value: 400, label: 'Regular (400)' },
        'medium': { value: 500, label: 'Medium (500)' },
        'semibold': { value: 600, label: 'SemiBold (600)' },
        'bold': { value: 700, label: 'Bold (700)' },
        'extrabold': { value: 800, label: 'ExtraBold (800)' },
        'black': { value: 900, label: 'Black (900)' }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setError('');
        setSuccess(false);
        
        if (!files.length) {
            setFontFiles([]);
            return;
        }

        // Validate all files
        const validFiles = [];
        for (const file of files) {
            const fileName = file.name.toLowerCase();
            const isSupported = supportedFormats.some(format => fileName.endsWith(format));
            
            if (!isSupported) {
                setError(`Unsupported file format in ${file.name}. Please use: ${supportedFormats.join(', ')}`);
                return;
            }

            // Validate file size (max 5MB per file)
            if (file.size > 5 * 1024 * 1024) {
                setError(`Font file ${file.name} too large. Maximum size is 5MB per file.`);
                return;
            }

            // Auto-detect weight from filename
            const detectedWeight = detectWeightFromFilename(file.name);
            validFiles.push({
                file: file,
                weight: detectedWeight,
                name: file.name
            });
        }

        setFontFiles(validFiles);
    };

    // Enhanced auto-detect font weight from filename
    const detectWeightFromFilename = (filename) => {
        const name = filename.toLowerCase();
        
        // Remove common prefixes and suffixes to focus on weight keywords
        const cleanName = name
            .replace(/\.(ttf|otf|woff|woff2)$/, '') // Remove extension
            .replace(/^.*[-_]/, ''); // Remove everything before last dash/underscore
        
        // More comprehensive weight detection patterns
        const weightPatterns = [
            { keywords: ['thin', '100'], weight: 'thin' },
            { keywords: ['ultralight', 'ultra-light', '200'], weight: 'thin' },
            { keywords: ['light', '300'], weight: 'light' },
            { keywords: ['regular', 'normal', '400'], weight: 'regular' },
            { keywords: ['medium', '500'], weight: 'medium' },
            { keywords: ['semibold', 'semi-bold', 'demi', 'demibold', '600'], weight: 'semibold' },
            { keywords: ['bold', '700'], weight: 'bold' },
            { keywords: ['extrabold', 'extra-bold', 'ultrabold', 'ultra-bold', '800'], weight: 'extrabold' },
            { keywords: ['black', 'heavy', '900'], weight: 'black' }
        ];
        
        // Check each pattern
        for (const pattern of weightPatterns) {
            if (pattern.keywords.some(keyword => name.includes(keyword) || cleanName.includes(keyword))) {
                console.log(`🎯 Weight detected: ${filename} → ${pattern.weight}`);
                return pattern.weight;
            }
        }
        
        // If no weight found, default to regular
        console.log(`📝 No weight detected in ${filename}, defaulting to regular`);
        return 'regular';
    };

    const sanitizeFontName = (name) => {
        return name
            .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, ' ')           // Normalize spaces
            .trim();
    };

    const detectFontFormat = (fileName) => {
        const extension = fileName.toLowerCase().split('.').pop();
        const formatMap = {
            'ttf': 'truetype',
            'otf': 'opentype',
            'woff': 'woff',
            'woff2': 'woff2'
        };
        return formatMap[extension] || 'truetype';
    };

    const handleUpload = async () => {
        if (!fontFiles.length || !fontName.trim()) {
            setError('Please select font files and enter a font family name.');
            return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
            setError('User not authenticated.');
            return;
        }

        const sanitizedName = sanitizeFontName(fontName);
        if (!sanitizedName) {
            setError('Please enter a valid font name.');
            return;
        }

        setUploading(true);
        setError('');
        setUploadProgress(0);

        try {
            const uploadedWeights = [];
            let totalProgress = 0;

            // Upload all font weight files
            for (let i = 0; i < fontFiles.length; i++) {
                const fontFileData = fontFiles[i];
                const { file, weight } = fontFileData;
                
                console.log(`📤 Uploading ${sanitizedName} - ${weight}:`, file.name);
                
                // Upload to Firebase Storage.
                // cacheControl: Firebase Storage liefert Uploads ohne explizite
                // Metadaten mit `private, max-age=0` aus — der Browser durfte
                // die Font-Datei also nie behalten und lud sie bei jedem
                // Seitenaufruf neu (gemessen: ~2,8 s pro Font, 10× parallel).
                // Fontdateien sind byte-immutable (gleicher Pfad = gleiche
                // Bytes), deshalb sicher 1 Jahr cachebar.
                const fileRef = storageRef(storage, `fonts/${currentUser.uid}/${file.name}`);
                const uploadTask = uploadBytesResumable(fileRef, file, {
                    cacheControl: 'public, max-age=31536000, immutable',
                });

                await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed', 
                        (snapshot) => {
                            const fileProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            const overallProgress = ((i * 100) + fileProgress) / fontFiles.length;
                            setUploadProgress(overallProgress);
                        },
                        (error) => {
                            console.error('Upload error:', error);
                            reject(error);
                        },
                        async () => {
                            try {
                                // Get download URL
                                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                
                                uploadedWeights.push({
                                    weight: weight,
                                    weightValue: fontWeights[weight].value,
                                    fileUrl: downloadURL,
                                    fileName: file.name,
                                    format: detectFontFormat(file.name)
                                });
                                
                                resolve();
                            } catch (error) {
                                reject(error);
                            }
                        }
                    );
                });
            }

            // Save font family metadata to Database
            const fontsRef = dbRef(db, `/users/${currentUser.uid}/customFonts`);
            const newFontRef = push(fontsRef);
            
            const fontFamilyData = {
                name: sanitizedName,
                weights: uploadedWeights,
                uploadedAt: new Date().toISOString(),
                totalFiles: uploadedWeights.length
            };
            
            await set(newFontRef, fontFamilyData);
            
            console.log(`🎯 Font family data to load:`, fontFamilyData);
            
            // Add the ID to the fontFamilyData so it can be tracked
            const fontFamilyDataWithId = {
                ...fontFamilyData,
                id: newFontRef.key
            };
            
            // Load font family with all weights using FontContext
            if (fontContext.loadCustomFont) {
                console.log(`🔄 Calling loadCustomFont for: ${sanitizedName}`);
                await fontContext.loadCustomFont(fontFamilyDataWithId);
            } else {
                console.error('❌ loadCustomFont not available in FontContext');
            }
            
            // Reset form
            setFontFiles([]);
            setFontName('');
            setUploadProgress(0);
            setSuccess(true);
            setUploading(false); // ✅ FIX: Reset uploading state
            
            // Callback to parent (this will trigger refresh)
            if (onFontUploaded) {
                onFontUploaded(fontFamilyDataWithId);
            }
            
            console.log('✅ Custom font family uploaded successfully:', sanitizedName, `(${uploadedWeights.length} weights)`);
            
            // Hide success message after 3 seconds
            setTimeout(() => setSuccess(false), 3000);
            
        } catch (error) {
            console.error('Error uploading font family:', error);
            setError('Upload failed. Please try again.');
            setUploading(false);
        }
    };


    return (
        <div className="custom-font-uploader">
            <div className="font-upload-container">
                <div className="upload-header">
                    <h4 className="upload-title">
                        <i className="fas fa-font"></i>
                        Upload Custom Font
                    </h4>
                    <p className="upload-description">
                        Upload your own font files (.ttf, .otf, .woff, .woff2). Font weights are automatically detected from filenames.
                    </p>
                </div>

                <div className="upload-form">
                    {/* Font Name Input */}
                    <div className="form-group">
                        <label className="form-label">Font Name</label>
                        <input
                            type="text"
                            value={fontName}
                            onChange={(e) => setFontName(e.target.value)}
                            placeholder="e.g., My Custom Font"
                            className="font-name-input"
                            disabled={uploading}
                        />
                    </div>

                    {/* File Upload */}
                    <div className="form-group">
                        <label className="form-label">Font Files</label>
                        <div className="upload-mode-selector">
                            <button 
                                className={`mode-btn ${uploadMode === 'single' ? 'active' : ''}`}
                                onClick={() => setUploadMode('single')}
                                type="button"
                            >
                                Single Weight
                            </button>
                            <button 
                                className={`mode-btn ${uploadMode === 'multi' ? 'active' : ''}`}
                                onClick={() => setUploadMode('multi')}
                                type="button"
                            >
                                Multiple Weights
                            </button>
                        </div>
                        
                        <div className="file-upload-area">
                            <input
                                type="file"
                                id="font-file-input"
                                accept=".ttf,.otf,.woff,.woff2"
                                onChange={handleFileChange}
                                className="file-input-hidden"
                                disabled={uploading}
                                multiple={uploadMode === 'multi'}
                            />
                            <label htmlFor="font-file-input" className={`file-upload-label ${fontFiles.length > 0 ? 'has-files' : ''}`}>
                                <i className="fas fa-cloud-upload-alt"></i>
                                <span className="upload-text">
                                    {fontFiles.length > 0 
                                        ? `${fontFiles.length} font file${fontFiles.length > 1 ? 's' : ''} selected`
                                        : uploadMode === 'multi' 
                                            ? 'Choose multiple font weights or drag & drop'
                                            : 'Choose font file or drag & drop'
                                    }
                                </span>
                                <span className="upload-hint">
                                    {uploadMode === 'multi' 
                                        ? 'Weights auto-detected from filenames (e.g., MyFont-Bold.ttf → Bold 700)'
                                        : 'Supports: TTF, OTF, WOFF, WOFF2 (max 5MB)'
                                    }
                                </span>
                            </label>
                        </div>

                        {/* Selected Files Preview with Auto-Detected Weights */}
                        {fontFiles.length > 0 && (
                            <div className="selected-files-preview">
                                <h5>
                                    <i className="fas fa-magic"></i>
                                    Auto-Detected Font Weights:
                                </h5>
                                <div className="files-list">
                                    {fontFiles.map((fontFileData, index) => (
                                        <div key={index} className="file-item">
                                            <div className="file-info">
                                                <span className="file-name">{fontFileData.file.name}</span>
                                                <div className="weight-detection">
                                                    <span className="detected-weight">
                                                        <i className="fas fa-arrow-right"></i>
                                                        {fontWeights[fontFileData.weight].label}
                                                    </span>
                                                    <span className="auto-detected-badge">Auto-detected</span>
                                                </div>
                                            </div>
                                            <button 
                                                className="remove-file-btn"
                                                onClick={() => {
                                                    const newFiles = fontFiles.filter((_, i) => i !== index);
                                                    setFontFiles(newFiles);
                                                    
                                                    // Re-generate font name if this was the first file
                                                    if (index === 0 && newFiles.length > 0) {
                                                        const baseName = newFiles[0].file.name.replace(/\.[^/.]+$/, "");
                                                        const weightKeywords = [
                                                            'thin', 'ultralight', 'ultra-light', 'light', 
                                                            'regular', 'normal', 'medium', 
                                                            'semibold', 'semi-bold', 'demi', 'demibold',
                                                            'bold', 'extrabold', 'extra-bold', 'ultrabold', 'ultra-bold',
                                                            'black', 'heavy',
                                                            '100', '200', '300', '400', '500', '600', '700', '800', '900'
                                                        ];
                                                        
                                                        let cleanName = baseName;
                                                        weightKeywords.forEach(keyword => {
                                                            const regex = new RegExp(`[-_]?${keyword}[-_]?`, 'gi');
                                                            cleanName = cleanName.replace(regex, '');
                                                        });
                                                        
                                                        cleanName = cleanName
                                                            .replace(/[-_]+/g, ' ')
                                                            .replace(/\s+/g, ' ')
                                                            .trim()
                                                            .replace(/\b\w/g, l => l.toUpperCase());
                                                        
                                                        if (cleanName) {
                                                            setFontName(cleanName);
                                                        }
                                                    }
                                                }}
                                                type="button"
                                                disabled={uploading}
                                                title="Remove file"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Weight Summary */}
                                <div className="weight-summary">
                                    <span className="summary-text">
                                        <i className="fas fa-info-circle"></i>
                                        {fontFiles.length} weight{fontFiles.length > 1 ? 's' : ''} detected for "{fontName || 'Custom Font'}"
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Upload Progress */}
                    {uploading && (
                        <div className="upload-progress">
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                            <span className="progress-text">
                                Uploading... {Math.round(uploadProgress)}%
                            </span>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-triangle"></i>
                            {error}
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <div className="success-message">
                            <i className="fas fa-check-circle"></i>
                            Font uploaded successfully! It's now available in your font selections.
                        </div>
                    )}

                    {/* Upload Button */}
                    <button
                        onClick={handleUpload}
                        disabled={!fontFiles.length || !fontName.trim() || uploading}
                        className={`upload-font-btn ${uploading ? 'uploading' : ''}`}
                    >
                        {uploading ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Uploading Font Family...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-upload"></i>
                                Upload Font {fontFiles.length > 1 ? 'Family' : ''}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomFontUploader;
